import { palette } from '../../styles/tokens';
import { formatPostal, canonicalPostal } from '../../utils/postal';

/**
 * PostalCodeInput — Canadian postal-code input primitive.
 *
 * Emits `A1A 1A1` via onChange. Raw user keystrokes are filtered to
 * alphanumerics, uppercased, and re-formatted live; the parent always sees
 * the formatted string. On blur, the canonical form is emitted (same as
 * live during typing since the formatter is deterministic).
 *
 * Uses autoComplete="postal-code" so browsers / password managers can
 * autofill address blocks. Enforces uppercase + clamped length at the JS
 * layer — browsers don't validate Canadian postal shape.
 *
 * Legacy data stored as lowercase or without a space is auto-upgraded on
 * next edit (the formatter normalizes any input shape).
 */

export interface PostalCodeInputProps {
  value: string;                       // current value (canonical or partial)
  onChange: (value: string) => void;   // emits formatted value live
  onBlur?: () => void;                 // fires after canonicalize-on-blur
  placeholder?: string;                // default "A1A 1A1"
  disabled?: boolean;
  error?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  id?: string;
  name?: string;
  className?: string;
  style?: React.CSSProperties;
}

const WRAPPER: React.CSSProperties = {
  minWidth: 0,
};

const INPUT_BASE: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

const INPUT_ERROR: React.CSSProperties = {
  borderColor: palette.red,
};

const INPUT_DISABLED: React.CSSProperties = {
  background: palette.g50,
  color: palette.g400,
  cursor: 'not-allowed',
};

const ERROR_TEXT: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: palette.red,
};

export function PostalCodeInput({
  value,
  onChange,
  onBlur,
  placeholder = 'A1A 1A1',
  disabled,
  error,
  ariaLabel,
  autoFocus,
  id,
  name,
  className,
  style,
}: PostalCodeInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(formatPostal(e.target.value));
  };

  const handleBlur = () => {
    const canonical = canonicalPostal(value);
    if (canonical !== value) onChange(canonical);
    onBlur?.();
  };

  const inputStyle: React.CSSProperties = {
    ...INPUT_BASE,
    ...(error ? INPUT_ERROR : {}),
    ...(disabled ? INPUT_DISABLED : {}),
  };

  return (
    <div className={className} style={{ ...WRAPPER, ...style }}>
      <input
        type="text"
        autoComplete="postal-code"
        value={value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        autoFocus={autoFocus}
        id={id}
        name={name}
        maxLength={7}
        style={inputStyle}
      />
      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
